import React, { useState, useEffect, useCallback } from "react";
import { Mail, MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, Empty, SectionHeader, Card, fmtDate } from "./shared";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const STATUS_OPTIONS = [
  { value: "new", label: "Nouveau" },
  { value: "contacted", label: "Contacté" },
  { value: "qualified", label: "Qualifié" },
  { value: "closed", label: "Clos" },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));

function mailtoFor(p) {
  const first = (p.contact_name || "").trim().split(/\s+/)[0] || "";
  const subject = "Votre demande de démonstration Lodge & Co";
  const body = [
    `Bonjour ${first},`,
    "",
    "Merci pour votre intérêt pour Lodge & Co et pour votre demande de démonstration.",
    "",
    "Afin de préparer un échange adapté à votre organisation, pourriez-vous nous indiquer vos principales disponibilités au cours des prochains jours ?",
    "",
    `Nous pourrons notamment vous présenter les fonctionnalités relatives à votre besoin : ${p.primary_need || ""}.`,
    "",
    "Bien cordialement,",
    "L’équipe Lodge & Co",
    "https://www.lodgeandco.com",
  ].join("\n");
  return `mailto:${encodeURIComponent(p.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function AdminProspects() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async (p) => {
    setData(null);
    try {
      const { data } = await api.get("/admin/prospects", { params: { page: p, limit: 20 } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Chargement impossible."));
      setData({ items: [], total: 0, page: 1, pages: 1 });
    }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  const changeStatus = async (id, status) => {
    try {
      const { data: res } = await api.patch(`/admin/prospects/${id}`, { status });
      setData((d) => ({ ...d, items: d.items.map((it) => (it.id === id ? res.prospect : it)) }));
      toast.success(`Statut : ${STATUS_LABEL[status]}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Mise à jour impossible."));
    }
  };

  if (!data) return <Loading />;

  return (
    <div data-testid="admin-prospects">
      <SectionHeader icon={Mail} title="Prospects" subtitle="Demandes de démonstration reçues depuis la page de connexion." />

      {(!data.items || data.items.length === 0) ? (
        <Empty label="Aucune demande — les demandes de démonstration apparaîtront ici." testid="prospects-empty" />
      ) : (
        <Card className="overflow-hidden p-0">
          <SelectableTable rows={data.items} rowId={(p) => p.id} filename="prospects.csv"
            ariaLabel="Prospects — sélection et défilement"
            csvColumns={[
              { label: "Date", get: (p) => fmtDate(p.created_at) },
              { label: "Contact", get: (p) => p.contact_name },
              { label: "E-mail", get: (p) => p.email },
              { label: "Organisation", get: (p) => p.organization },
              { label: "Type", get: (p) => p.organization_type },
              { label: "Besoin", get: (p) => p.primary_need },
              { label: "Source", get: (p) => p.utm_source || p.source || "" },
              { label: "Statut", get: (p) => p.status },
            ]}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2.5"><SelectAllHeaderCheckbox /></th>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Contact</th>
                  <th className="px-3 py-2.5">E-mail</th>
                  <th className="px-3 py-2.5">Organisation</th>
                  <th className="px-3 py-2.5">Type</th>
                  <th className="px-3 py-2.5">Besoin</th>
                  <th className="px-3 py-2.5">Source</th>
                  <th className="px-3 py-2.5">Statut</th>
                  <th className="px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr className="border-b border-slate-100 align-top" data-testid={`prospect-row-${p.id}`}>
                      <td className="px-3 py-3"><RowCheckbox id={p.id} label={`Sélectionner ${p.email}`} /></td>
                      <td className="whitespace-nowrap px-3 py-3 text-slate-500">{fmtDate(p.created_at)}</td>
                      <td className="px-3 py-3 font-medium text-[#17201A]">{p.contact_name}</td>
                      <td className="px-3 py-3 text-slate-600">{p.email}</td>
                      <td className="px-3 py-3 text-slate-600">{p.organization}</td>
                      <td className="px-3 py-3 text-slate-500">{p.organization_type}</td>
                      <td className="px-3 py-3 text-slate-500">{p.primary_need}</td>
                      <td className="px-3 py-3 text-slate-400">{p.utm_source || p.source || "—"}</td>
                      <td className="px-3 py-3">
                        <Select value={p.status} onValueChange={(v) => changeStatus(p.id, v)}>
                          <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`prospect-status-${p.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <a
                            href={mailtoFor(p)}
                            data-testid={`prospect-contact-${p.id}`}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-semibold text-[#1E3D2B] hover:bg-slate-50"
                          >
                            <Mail size={13} /> Contacter
                          </a>
                          {p.message && (
                            <button
                              type="button"
                              onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                              data-testid={`prospect-message-${p.id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                            >
                              <MessageSquare size={13} /> Message
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === p.id && p.message && (
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <td colSpan={10} className="px-3 py-3 text-sm text-slate-600" data-testid={`prospect-message-body-${p.id}`}>
                          <span className="font-semibold text-slate-500">Message : </span>{p.message}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </SelectableTable>

          <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2.5 text-xs text-slate-500">
            <span>{data.total} demande(s) · page {data.page}/{data.pages || 1}</span>
            <div className="flex items-center gap-1.5">
              <button
                type="button" disabled={data.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="prospects-prev"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1.5 font-medium disabled:opacity-40"
              >
                <ChevronLeft size={14} /> Précédent
              </button>
              <button
                type="button" disabled={data.page >= (data.pages || 1)}
                onClick={() => setPage((p) => p + 1)}
                data-testid="prospects-next"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1.5 font-medium disabled:opacity-40"
              >
                Suivant <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
